import { Component, OnInit } from '@angular/core';
import { FormGroup, FormBuilder } from '@angular/forms';
import { Router, ActivatedRoute } from '@angular/router';
import { ApplicationService } from 'src/app/shared/services/application.service';
import { SingleResultApiResponse } from 'src/app/shared/models/api-response.model';
import { Correspondent, DefaultCorrespondent } from '../models/corresondents';
import { ErrorResponse } from 'src/app/shared/models/error.model';
import { Observable } from 'rxjs';
import { ModalService } from 'src/app/shared/services/modal.service';
import { compareDefaultCorrespondents } from '../shared/utils/ModelUtils';
import { Store } from '@ngrx/store';
import { Action_ApplicationSectionSaved } from '../state/actions';

@Component({
  selector: 'online-app-default-correspondent',
  templateUrl: './default-correspondent.component.html',
  styleUrls: ['./default-correspondent.component.scss']
})
export class DefaultCorrespondentComponent implements OnInit {

  defaultCorrespondentFormGroup: FormGroup;
  isFormSubmitted = false;
  applicationId: number;
  defaultCorrespondentId: number;
  defaultCorrespondentIsAgent: boolean;
  correspondents: Correspondent[] = [];
  initialCorrespondent: DefaultCorrespondent;
  defaultCorrespondentError = '';

  isSaveSuccessful = false;
  notAvailableText = 'Not Available';
  isSubmittedApplication = false;

  constructor(private formBuilder: FormBuilder,
    private router: Router,
    private store: Store<any>,
    private modalService: ModalService,
    private applicationService: ApplicationService,
    private activatedRoute: ActivatedRoute) {

    this.activatedRoute.parent.params.subscribe(params => {
      this.applicationId = +params['id'];
    });

  }

  ngOnInit() {

    this.defaultCorrespondentFormGroup = this.formBuilder.group({
      correspondentId: 0
    });

    this.applicationService.getAllCorrespondents(this.applicationId)
      .subscribe((response: SingleResultApiResponse) => {
        if (response.Result) {
          this.correspondents = <Correspondent[]>response.Result;
          this.updateFormValuesFromCorrespondents(response.Result);
          this.initialCorrespondent = this.getDefaultCorrespondentDetails();
        }
      }, err => {
        console.error(err);
      });

    this.applicationService.getApplicationDetails(this.applicationId)
      .subscribe((response: SingleResultApiResponse) => {
        if (response.Result) {

          this.isSubmittedApplication = response.Result.ApplicationStatus === 'Submitted';

          if (this.isSubmittedApplication) {
            this.defaultCorrespondentFormGroup.disable();
          }

        }
      }, err => {
        console.error(err);
      });

  }

  setDefaultCorrespondent(correspondentId: number, isAgent: boolean) {
    this.defaultCorrespondentId = correspondentId;
    this.defaultCorrespondentIsAgent = isAgent;
  }

  previous() {
    this.router.navigate(['/applications', this.applicationId, 'edit', 'special-circumstances']);
  }
  next() {
    this.router.navigate(['/applications', this.applicationId, 'edit', 'supporting-documents']);
  }

  saveAndContinue() {
    this.isFormSubmitted = true;

    const defaultCorrespondent = this.getDefaultCorrespondentDetails();

    this.applicationService.setDefaultCorrespondent(this.applicationId, defaultCorrespondent)
      .subscribe((response: SingleResultApiResponse) => {
        this.dispatchSectionSaveAction();
        this.isSaveSuccessful = true;
        this.router.navigate(['/applications', this.applicationId, 'edit', 'supporting-documents']);
      }, (errorResponse: ErrorResponse) => {
        const { errors } = errorResponse;
        for (const error of errors) {
          this.defaultCorrespondentError = error.Value;
          //console.log(`Error in updating Default correspondent details.${error.Key},${error.Value}`);
        }
        this.dispatchSectionSaveAction();
        this.initialCorrespondent = this.getDefaultCorrespondentDetails();
      });

  }

  dispatchSectionSaveAction() {
    this.store.dispatch({
      type: Action_ApplicationSectionSaved
    });
  }

  canDeactivate(): Observable<boolean> | boolean {

    if (this.isSaveSuccessful) {
      return true;
    }

    if (!this.isDefaultCorrespondentDetailsSame()) {
      this.modalService.showModal();
      return this.modalService.stayOnThePage;
    } else {
      this.modalService.hideModal();
      return true;
    }
  }

  isCorrespondentToBeHighlighted(correspondent: Correspondent): boolean {
    let isCorrespondentToBeHighlighted = false;

    if (correspondent.CorrespondentId === this.defaultCorrespondentId) {
      isCorrespondentToBeHighlighted = true;
    } else if (correspondent.IsDefault && !this.defaultCorrespondentId) {
      isCorrespondentToBeHighlighted = true;
    }

    return isCorrespondentToBeHighlighted;
  }

  isCorrespondentToBeDisabled(correspondent: Correspondent): boolean {
    return correspondent.DisableForSelection === true;
  }


  getDefaultCorrespondentDetails(): DefaultCorrespondent {

    const defaultCorrespondent = new DefaultCorrespondent();
    defaultCorrespondent.CorrespondentId = this.defaultCorrespondentId;
    defaultCorrespondent.IsAgent = this.defaultCorrespondentIsAgent;
    return defaultCorrespondent;

  }

  getHomePhoneNumber(correspondent: Correspondent) {
    const { HomeCountryCode, HomePhoneNumber, MobileCountryCode, MobilePhoneNumber } = correspondent;

    if (HomeCountryCode || HomePhoneNumber) {
      if (correspondent.IsAgent === true) {
        return `${HomePhoneNumber}`;
      }
      return `${HomeCountryCode} ${HomePhoneNumber}`;
    } else {
      return this.notAvailableText;
    }

  }

  getMobilePhoneNumber(correspondent: Correspondent) {

    const { MobileCountryCode, MobilePhoneNumber } = correspondent;

    if (MobileCountryCode || MobilePhoneNumber) {
      return `${MobileCountryCode} ${MobilePhoneNumber}`;
    } else {
      return this.notAvailableText;
    }

  }

  getEmailAddress(correspondent: Correspondent) {

    if (!correspondent.EmailAddress) {
      return this.notAvailableText;
    }

    return correspondent.EmailAddress;
  }

  // getAgentNote(){
  //   var agentNote = "Important: Please note that all invoices and documents will be sent to the agent until the ATE is successfully returned from school";
  //   return agentNote;
  // }
  getAgentNote(correspondent: Correspondent): string {
    return correspondent.AgentNote;
  }
  getAgentNotePrefix(correspondent: Correspondent): string {
    if (correspondent.AgentNote !== '') {
      return 'Important: ';
    } else {
      return '';
    }
  }

  isDefaultCorrespondentDetailsSame(): boolean {

    const currentDefaultCorrespondent = this.getDefaultCorrespondentDetails();
    const initialDefaultCorrespondent = this.initialCorrespondent;

    const isDefaultCorrespondentDetailsSame =
      compareDefaultCorrespondents(initialDefaultCorrespondent, currentDefaultCorrespondent);

    return isDefaultCorrespondentDetailsSame;

  }

  updateFormValuesFromCorrespondents(correspondents: Correspondent[]): void {
    const defaultCorrespondent = correspondents.find(c => c.IsDefault);

    if (defaultCorrespondent) {
      this.defaultCorrespondentId = defaultCorrespondent.CorrespondentId;
      this.defaultCorrespondentIsAgent = defaultCorrespondent.IsAgent;
    }

  }

  //show border when tab on link for accessibility
  linkTabAccessibility(event): void {
    event.style.outline = "1px solid black";
  }

  linkBlurAccessibility(event): void {
    event.style.outline = "";
  }
}
